import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { sortByDate } from '../../../utilities/time';
import { deleteDocument, archiveDocument, restoreDocument, setReplaceDocument, getDocumentDetails } from '../../../reducers/documents';
import DocumentReader from './../reader';
import { downloadFile } from '../../../utilities/download';
import { getFileSize, getFileType } from '../../../utilities/common';
import DocumentViewerHeader from './header';
import { dateToFloatingString } from '../../../utilities/dateTime';
import { DocumentInfo } from '../../../utilities/documentTypes';

import './../document.css';
import messages from '../../../text/messages';

class DocumentViewer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            signer: undefined,
            reader: undefined,
            sort: {
                property: 'created_at',
                sortFunc: sortByDate,
                ascending: true
            }
        };

        this.sortDocuments = this.sortDocuments.bind(this);
        this.renderActions = this.renderActions.bind(this);
        this.renderComponent = this.renderComponent.bind(this);
        this.renderDocumentRow = this.renderDocumentRow.bind(this);

        // reader
        this.showReader = this.showReader.bind(this);
        this.closeReader = this.closeReader.bind(this);

        // signer
        this.showSigner = this.showSigner.bind(this);
    }

    showReader(document) {
        this.setState({ ...this.state, reader: document });
    }

    showSigner(document, index) {
        this.setState({ ...this.state, signer: { index: index, document: document } });
    }

    closeReader() {
        this.setState({ ...this.state, reader: undefined });
    }

    sortDocuments(prop, func) {
        const { property, ascending } = this.state.sort;
        let direction = property === prop ? !ascending : true;

        this.setState({ ...this.state, sort: { property: prop, sortFunc: func, ascending: direction } });
    }

    renderActions(document, index) {
        const {
            id,
            sign,
            download,
            remove,
            archive,
            upload,
            deleteDocument,
            archiveDocument,
            restoreDocument,
            setReplaceDocument,
            getDocuments,
            getDocumentDetails
        } = this.props;

        // Sign
        let isDocumentSigned = sign && sign.signatures && sign.signatures.filter(s => s.doc_id === document.id).length > 0;
        let signControl =
            sign && isDocumentSigned ? (
                <span className="doc-signed">
                    <FormattedMessage id={'docSigned'} defaultMessage={'Document already signed'} />
                </span>
            ) : (
                <i className="material-icons" onClick={() => this.showSigner(document, index)}>
                    edit
                </i>
            );

        // Archive
        let archiveControl =
            document.status === 'archived' ? (
                <i className="material-icons" onClick={() => restoreDocument(document.id, () => getDocuments())}>
                    unarchive
                </i>
            ) : (
                <i className="material-icons" onClick={() => archiveDocument(document.id, () => getDocuments())}>
                    archive
                </i>
            );

        return (
            <div className="col-sm-2 col-xs-4 text-right no-select column">
                {/* sign */}
                {sign && signControl}
                {archive && archiveControl}
                {/* delete */}
                {remove && (
                    <i className="material-icons" onClick={() => deleteDocument(document.id, () => getDocuments())}>
                        delete
                    </i>
                )}
                {download && document.s3_url ? (
                    <a href={document.s3_url} target="_blank" rel="noopener noreferrer">
                        <i className="material-icons download">publish</i>
                    </a>
                ) : (
                    <i className="material-icons download" onClick={() => getDocumentDetails(document.id, undefined, details => downloadFile(details.s3_url))}>
                        publish
                    </i>
                )}
                {/* re-upload */}
                {upload && (
                    <i className="material-icons" onClick={() => setReplaceDocument(document, id)}>
                        create
                    </i>
                )}
            </div>
        );
    }

    renderComponent(view) {
        const { documents, id, upload, setReplaceDocument, deleteDocument, getDocuments, remove } = this.props;
        const { component, removeCalback, readOnly } = view;

        if (component === 'icon-download') {
            return (
                <div className="document-viewer-icon-download">
                    {documents.map((d, i) => {
                        const info = DocumentInfo.findByType(d.doc_type);
                        const name = d.display_name || (info ? info.displayName : undefined) || d.doc_type;
                        return (
                            <div key={i} className="document-viewer-icon-download-row">
                                <div className="doc-name-wrapper">
                                    <a href={d.s3_url} target="_blank" rel="noopener noreferrer">
                                        <div className="name ellipsis">
                                            <i className="material-icons">description</i>
                                            <span>{name}</span>
                                        </div>
                                    </a>
                                    {upload && (
                                        <div className="name-sub">
                                            {d.name && <span>{d.filename.split('/')[1]} | </span>}
                                            {d.size ? getFileSize(d.size) : null}
                                        </div>
                                    )}
                                </div>
                                {(upload || remove) && (
                                    <div className="doc-actions">
                                        {upload && (
                                            <span className="document-viewer-icon-download-update">
                                                <i className="material-icons" onClick={() => setReplaceDocument(d, id)}>
                                                    create
                                                </i>
                                            </span>
                                        )}
                                        {remove && (
                                            <span className="document-viewer-icon-download-update">
                                                <i className="material-icons" onClick={() => deleteDocument(d.id, () => getDocuments(() => null))}>
                                                    delete
                                                </i>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }
        if (component === 'upload-later') {
            return (
                <div className="document-viewer-icon-download">
                    {documents.map((d, i) => {
                        const info = DocumentInfo.findByType(d.doc_type);
                        const name = d.display_name || (info ? info.displayName : undefined) || d.doc_type;
                        return (
                            <div key={i} className="document-viewer-icon-download-row document-actions">
                                <div className="upload-later">
                                    <a href={d.s3_url} target="_blank" rel="noopener noreferrer">
                                        <div className="name">
                                            <i className="material-icons">description</i>
                                            <span>{name}</span>
                                        </div>
                                    </a>
                                    <div className="name-sub">
                                        {d.name && <span>{d.filename.split('/')[1]} | </span>}
                                        {d.size ? getFileSize(d.size) : null}
                                    </div>
                                </div>
                                {upload &&
                                    !(typeof readOnly === 'function' ? readOnly(d) : readOnly) && (
                                        <div
                                            onClick={() => deleteDocument(d.id, removeCalback ? () => removeCalback(d) : () => getDocuments(() => null))}
                                            className="action"
                                        >
                                            <FormattedMessage {...messages.remove} />
                                        </div>
                                    )}
                            </div>
                        );
                    })}
                </div>
            );
        }
    }

    renderDocumentRow(d, i) {
        const { documents, configuredDisplayName } = this.props;
        const info = DocumentInfo.findByType(d.doc_type);
        const resolvedFileName = info ? info.displayName : null;
        const fileName = d.display_name || configuredDisplayName || resolvedFileName || d.doc_type;
        const signatureIncluded = documents.find(d => d.signature) !== undefined;

        return (
            <div key={i} className="col-xs-12 table-row">
                <div className={`col-sm-${signatureIncluded ? 4 : 5} col-xs-8`}>
                    <a className="name ellipsis" href={d.s3_url} target="_blank" rel="noopener noreferrer" title={configuredDisplayName}>
                        {fileName}
                    </a>
                    <div className="name-sub">
                        {d.name && <span>{d.filename.split('/')[1]} | </span>}
                        {d.content_type ? getFileType(d) : null}
                        {d.content_type && d.size ? <span> | </span> : null}
                        {d.size ? getFileSize(d.size) : null}
                    </div>
                </div>
                <div className={`col-sm-${signatureIncluded ? 2 : 3} hidden-xs column`}>
                    <div className="ellipsis">{d.ref_name || d.ref_id}</div>
                </div>
                {signatureIncluded && (
                    <div className="col-sm-2 hidden-xs column">
                        <div className="ellipsis">{d.signature ? dateToFloatingString(d.signature.updated_at) : null}</div>
                    </div>
                )}
                <div className="col-sm-2 hidden-xs column">
                    <div className="ellipsis">{dateToFloatingString(d.updated_at)}</div>
                </div>
                {this.renderActions(d, i)}
            </div>
        );
    }

    render() {
        const { documents, view } = this.props;
        const { reader, sort } = this.state;
        const { property, sortFunc, ascending } = sort;

        if (!view || !documents || documents.length === 0 || documents[0].error) {
            return null;
        }

        const { showHeader, component } = view;
        sortFunc(documents, property, ascending);

        if (component) {
            return this.renderComponent(view);
        }

        return (
            <div className="row-container">
                {reader && <DocumentReader document={reader} onClose={this.closeReader} />}
                <div className="row document-viewer">
                    {showHeader !== false && (
                        <DocumentViewerHeader
                            ascending={ascending}
                            property={property}
                            sortDocuments={this.sortDocuments}
                            includeSignature={documents.find(d => d.signature) !== undefined}
                        />
                    )}
                    <div className="col-xs-12" style={{ marginBottom: `${showHeader !== false ? 15 : 0}px` }}>
                        {documents && documents.length > 0 && documents.map((d, i) => this.renderDocumentRow(d, i))}
                    </div>
                </div>
            </div>
        );
    }
}

DocumentViewer.defaultProps = {
    id: undefined,
    documents: undefined,
    view: undefined,
    download: true,
    remove: undefined,
    archive: undefined,
    sign: undefined,
    upload: undefined,
    getDocuments: undefined,
    component: undefined,
    configuredDisplayName: undefined,
    providedDocuments: undefined
};
DocumentViewer.propTypes = {
    id: PropTypes.string.isRequired,
    documents: PropTypes.array,
    view: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    download: PropTypes.bool,
    remove: PropTypes.bool,
    archive: PropTypes.bool,
    sign: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    upload: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    getDocuments: PropTypes.func.isRequired,
    component: PropTypes.any,
    configuredDisplayName: PropTypes.any,
    providedDocuments: PropTypes.array
};

const mapStateToProps = (state, props) => ({
    role: state.identity.role,
    documents: props.providedDocuments || (state.documents[props.id] ? state.documents[props.id].list : undefined)
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            deleteDocument: deleteDocument,
            archiveDocument: archiveDocument,
            restoreDocument: restoreDocument,
            setReplaceDocument: setReplaceDocument,
            getDocumentDetails: getDocumentDetails
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(DocumentViewer);



// WEBPACK FOOTER //
// ./src/components/document/viewer/index.jsx